/**
 * Product page interactions, ported from src/pages/ProductDetail.tsx.
 *
 * Variant resolution is done against a JSON map of the product's variants so
 * option buttons behave like the React version (click any value, the matching
 * variant is selected) rather than Shopify's default select elements.
 *
 * The sticky bar's dropdowns write into the SAME `selected` state as the main
 * option buttons, so the two never disagree — picking a colour in the bar
 * repaints the buttons up the page and vice versa.
 */
(function () {
  const root = document.querySelector('[data-cs-product]');
  if (!root) return;

  const variants = window.csProductVariants || [];
  const idInput = root.querySelector('[data-cs-variant-id]');
  const addBtn = root.querySelector('[data-cs-add]');
  const addLabel = root.querySelector('[data-cs-add-label]');
  const bar = document.querySelector('[data-cs-sticky]');

  /** Currently selected value per option position. */
  const selected = {};
  root.querySelectorAll('[data-cs-option-value][aria-pressed="true"]').forEach((b) => {
    selected[b.dataset.optionPosition] = b.dataset.value;
  });

  /**
   * Whether an option value is purchasable given the other current selections.
   * Mirrors isOptionValueAvailable in ProductDetail.tsx.
   */
  const valueAvailable = (pos, val) =>
    variants.some(
      (v) =>
        v.available &&
        v.options.every((o, i) =>
          String(i + 1) === String(pos) ? o === val : selected[String(i + 1)] === o
        )
    );

  const paint = (btn, on) => {
    btn.setAttribute('aria-pressed', String(on));
    btn.classList.toggle('bg-foreground', on);
    btn.classList.toggle('text-background', on);
    btn.classList.toggle('border-foreground', on);
    btn.classList.toggle('border-border', !on);
    btn.classList.toggle('btn-fill', !on);
    btn.classList.toggle('hover:text-primary-foreground', !on);
    const chip = btn.querySelector('span[aria-hidden]');
    if (chip) {
      chip.classList.toggle('border-background', on);
      chip.classList.toggle('border-foreground', !on);
    }
  };

  /** Repaint one sticky-bar dropdown to match `selected`. */
  const paintSelect = (wrap) => {
    const pos = wrap.dataset.optionPosition;
    const val = selected[pos];
    const label = wrap.querySelector('[data-cs-vselect-label]');
    const swatch = wrap.querySelector('[data-cs-vselect-swatch]');
    if (label) label.textContent = val || '';

    wrap.querySelectorAll('[data-cs-vselect-item]').forEach((item) => {
      const isSel = item.dataset.value === val;
      item.setAttribute('aria-selected', String(isSel));
      const check = item.querySelector('[data-cs-vselect-check]');
      if (check) check.classList.toggle('hidden', !isSel);

      const off = !valueAvailable(pos, item.dataset.value);
      item.title = off ? 'Unavailable with current selection' : '';
      item.classList.toggle('text-muted-foreground', off);
      item.classList.toggle('line-through', off);
      item.classList.toggle('opacity-40', off);
      item.classList.toggle('hover:bg-primary', !off);
      item.classList.toggle('hover:text-primary-foreground', !off);

      if (isSel && swatch) {
        const img = item.dataset.swatchImage;
        const col = item.dataset.swatchColor;
        swatch.classList.toggle('hidden', !img && !col);
        swatch.style.backgroundImage = img ? 'url(' + img + ')' : '';
        swatch.style.background = !img && col ? col : swatch.style.background;
      }
    });
  };

  /** Grey out and disable main option buttons that have no purchasable variant. */
  const paintMainAvailability = () => {
    root.querySelectorAll('[data-cs-option-value]').forEach((btn) => {
      const off = !valueAvailable(btn.dataset.optionPosition, btn.dataset.value);
      btn.disabled = off;
      btn.setAttribute('aria-disabled', String(off));
      btn.title = off ? 'Not available with current selection' : '';
      btn.classList.toggle('opacity-40', off);
      btn.classList.toggle('line-through', off);
      btn.classList.toggle('cursor-not-allowed', off);
    });
  };

  const sync = () => {
    const match = variants.find((v) =>
      v.options.every((val, i) => selected[String(i + 1)] === val)
    );

    paintMainAvailability();
    document.querySelectorAll('[data-cs-vselect]').forEach(paintSelect);

    if (bar) {
      const positions = (bar.dataset.displayPositions || '')
        .split(',')
        .filter(Boolean);
      const summary = positions.map((p) => selected[p]).filter(Boolean).join(' · ');
      const summaryEl = bar.querySelector('[data-cs-sticky-options]');
      if (summaryEl) summaryEl.textContent = summary;
    }

    if (!match) return;
    if (idInput) idInput.value = match.id;
    if (addBtn) addBtn.disabled = !match.available;
    if (addLabel) addLabel.textContent = match.available ? 'Add to cart' : 'Sold out';

    const label = match.available ? 'Add to cart' : 'Sold out';
    document.querySelectorAll('[data-cs-price], [data-cs-sticky-price]').forEach((el) => {
      el.textContent = match.price;
    });
    document.querySelectorAll('[data-cs-sticky-add]').forEach((b) => {
      b.disabled = !match.available;
    });
    document.querySelectorAll('[data-cs-sticky-label]').forEach((el) => {
      el.textContent = label;
    });

    if (match.featured_image) {
      const main = document.querySelector('[data-cs-main-image]');
      if (main) main.src = match.featured_image;
    }
  };

  /** Single entry point for a value change, wherever it came from. */
  const choose = (pos, value) => {
    selected[pos] = value;
    // If the other options no longer fit the new choice (e.g. switching to a
    // model that doesn't come in the picked colour), move them to the first
    // value that does, so the page never rests on a variant that doesn't exist.
    root.querySelectorAll('[data-cs-option]').forEach((el) => {
      const p = el.dataset.optionPosition;
      if (p === String(pos) || valueAvailable(p, selected[p])) return;
      const fit = [...el.querySelectorAll('[data-cs-option-value]')]
        .map((b) => b.dataset.value)
        .find((v) => valueAvailable(p, v));
      if (fit) selected[p] = fit;
    });
    root.querySelectorAll('[data-cs-option-value]').forEach((b) =>
      paint(b, selected[b.dataset.optionPosition] === b.dataset.value)
    );
    sync();
  };

  root.querySelectorAll('[data-cs-option-value]').forEach((btn) => {
    btn.addEventListener('click', () => choose(btn.dataset.optionPosition, btn.dataset.value));
  });

  // --- Sticky-bar dropdowns -------------------------------------------------
  const closeAll = (except) => {
    document.querySelectorAll('[data-cs-vselect]').forEach((w) => {
      if (w === except) return;
      w.querySelector('[data-cs-vselect-panel]').classList.add('hidden');
      w.querySelector('[data-cs-vselect-trigger]').setAttribute('aria-expanded', 'false');
      w.querySelector('[data-cs-vselect-chevron]').classList.remove('rotate-180');
    });
  };

  document.querySelectorAll('[data-cs-vselect]').forEach((wrap) => {
    const trigger = wrap.querySelector('[data-cs-vselect-trigger]');
    const panel = wrap.querySelector('[data-cs-vselect-panel]');
    const chevron = wrap.querySelector('[data-cs-vselect-chevron]');

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = panel.classList.contains('hidden');
      closeAll(wrap);
      panel.classList.toggle('hidden', !open);
      trigger.setAttribute('aria-expanded', String(open));
      chevron.classList.toggle('rotate-180', open);
    });

    wrap.querySelectorAll('[data-cs-vselect-item]').forEach((item) => {
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        choose(wrap.dataset.optionPosition, item.dataset.value);
        closeAll();
      });
    });
  });

  document.addEventListener('click', () => closeAll());
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAll();
  });

  // --- Quantity -------------------------------------------------------------
  const qtyInput = root.querySelector('[data-cs-qty-input]');
  const mirrorQty = () => {
    const v = String(Math.max(1, parseInt(qtyInput.value, 10) || 1));
    qtyInput.value = v;
    document.querySelectorAll('[data-cs-qty-mirror]').forEach((el) => {
      el.textContent = v;
    });
  };
  document.querySelectorAll('[data-cs-qty]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const step = parseInt(btn.dataset.csQty, 10);
      qtyInput.value = String(Math.max(1, (parseInt(qtyInput.value, 10) || 1) + step));
      mirrorQty();
    });
  });
  if (qtyInput) qtyInput.addEventListener('input', mirrorQty);

  root.querySelectorAll('[data-cs-thumb]').forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const main = document.querySelector('[data-cs-main-image]');
      if (main && thumb.dataset.full) main.src = thumb.dataset.full;
    });
  });

  // --- Reveal the sticky bar ------------------------------------------------
  // Shown once the inline Add-to-cart button is ~half off the top of the
  // screen, matching ProductDetail.tsx. A rAF-throttled scroll listener is used
  // rather than IntersectionObserver so it stays correct across scroll jumps.
  //
  // Horizon scrolls .page-wrapper, not the window, so the listener goes on that
  // element — the same reason the sticky header needed fixing.
  if (bar && addBtn) {
    const scroller = document.querySelector('.page-wrapper');
    let raf = 0;
    let shown = null;
    const update = () => {
      raf = 0;
      const rect = addBtn.getBoundingClientRect();
      const show = rect.top + rect.height / 2 < 0;
      if (show === shown) return;
      shown = show;
      // The consent banner sits above the bar only while the bar is showing.
      document.documentElement.style.setProperty('--cs-sticky-banner-h', show ? bar.offsetHeight + 'px' : '0px');
      bar.classList.toggle('translate-y-full', !show);
      bar.classList.toggle('translate-y-0', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.querySelectorAll('button').forEach((b) => {
        b.tabIndex = show ? 0 : -1;
      });
      if (!show) closeAll();
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    // Publish the bar's height ONCE, and mark the page as carrying a sticky
    // bar. card-slinger-brand.css uses both to park the Inbox chat launcher and
    // the Google merchant widget above the bar permanently. Deliberately not
    // toggled with the bar's visibility — doing that made the launcher hop
    // around mid-scroll.
    document.documentElement.style.setProperty('--cs-sticky-h', bar.offsetHeight + 'px');
    document.documentElement.classList.add('cs-sticky-page');

    if (scroller) scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();

    const edit = bar.querySelector('[data-cs-sticky-edit]');
    const firstOption = root.querySelector('[data-cs-option]');
    if (edit && firstOption) {
      edit.addEventListener('click', () => {
        firstOption.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  sync();
})();
